import _ from 'lodash';
import React, { useEffect, useState, useMemo } from 'react';
import { Input } from 'semantic-ui-react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import useDebounce from '../../../../core/hooks/use-debounce';

import {
  Modal,
  Container,
  ContainerButton,
  InviteButton,
  CancelButton,
  Text,
  Dropdown,
  Form,
  TextArea,
  Label,
} from './styles';

import {
  DEBOUNCE_TIME,
  ORDER_PROJECTS_BY,
  PROJECTS,
  PROJECTS_LIMIT,
} from '../graphql';

const defaultSubject = "You're invited to participate in a PlaybookUX study";
const defaultMessage = `Hi {{name}},\n\nYou've been invited to participate in a {{amount}} study.\n\n
                        Log into PlaybookUX to see if you qualify.\n\n{{login}}\n\nThe PlaybookUX Team`;

const InviteTester = (props) => {
  /** props */
  const {
    open,
    onClose,
    onClickInvite,
    disabledInvite,
  } = props;

  /** states */
  const [selectedSegmentIdValue, setSelectedSegmentIdValue] = useState();
  const [selectedProjectIdValue, setSelectedProjectIdValue] = useState();
  const [chars, setChars] = useState('');
  const [debouncedSearch] = useDebounce(() => chars, DEBOUNCE_TIME, [chars]);
  const [subject, setSubject] = useState(defaultSubject);
  const [message, setMessage] = useState(defaultMessage);

  const {
    loading,
    data = {},
  } = useQuery(PROJECTS, {
    variables: {
      status: 'in_progress',
      participantsSource: ['playbook'],
      name: debouncedSearch,
      order: ORDER_PROJECTS_BY,
      limit: PROJECTS_LIMIT,
    },
    skip: !open,
    fetchPolicy: 'no-cache',
  });

  const handleChange = (e, { value }) => {
    const projectBySegment = data && data.projects.find((project) => project.segments.find((segment) => segment.id === value));
    setSelectedProjectIdValue(projectBySegment.id);
    setSelectedSegmentIdValue(value);
  };

  const handleSearchChange = (e, { searchQuery }) => {
    setChars(_.trim(searchQuery));
  };

  const handleClose = () => {
    setSelectedSegmentIdValue(null);
    setChars('');
    onClose();
  };

  const options = useMemo(() => {
    const values = data.projects && data.projects.reduce((acc, project) => {
      const projectSegments = project.segments.map((segment) => ({
        key: segment.id,
        value: segment.id,
        text: project.segments.length > 1 ? `${project.name}(${segment.name})` : project.name,
      }));
      acc.push(...projectSegments);
      return acc;
    }, []);
    return values;
  }, [data]);

  useEffect(() => {
    if (!_.isEmpty(options)) {
      const firstSegmentId = _.first(options).value;
      const firstProject = data && data.projects.find((project) => project.segments.find((segment) => segment.id === firstSegmentId));
      setSelectedProjectIdValue(firstProject.id);
      setSelectedSegmentIdValue(firstSegmentId);
    }
  }, [options, data]);

  const handleInvite = () => {
    onClickInvite(selectedProjectIdValue, selectedSegmentIdValue, subject, message);
  };

  return (
    <Modal
      size="small"
      open={open}
      onClose={handleClose}
    >
      <Container>
        <Form>
          <Text>
            Invite participants to a project
          </Text>

          <Dropdown
            loading={loading}
            onChange={handleChange}
            search
            selection
            onSearchChange={handleSearchChange}
            options={options}
            value={selectedSegmentIdValue}
          />

          <Form.Field>
            <Label>Subject line</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
          </Form.Field>

          <Form.Field>
            <Label>Email</Label>
            <TextArea value={message} onChange={(e) => setMessage(e.target.value)} rows={10} />
          </Form.Field>

          <ContainerButton>
            <CancelButton onClick={handleClose}>
              Cancel
            </CancelButton>
            <InviteButton
              onClick={handleInvite}
              disabled={(disabledInvite || !selectedProjectIdValue || !selectedSegmentIdValue)}
            >
              Invite
            </InviteButton>
          </ContainerButton>
        </Form>
      </Container>
    </Modal>

  );
};

InviteTester.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onClickInvite: PropTypes.func.isRequired,
  disabledInvite: PropTypes.bool,
};

InviteTester.defaultProps = {
  open: false,
  disabledInvite: false,
};

export default InviteTester;
